// frontend\src\pages\Favorites\Favorites.js
import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { BiCopy, BiSolidStar, BiArrowBack } from "react-icons/bi";

const Favorites = () => {
  const { isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [dilutionFilter, setDilutionFilter] = useState("");
  const [isoFilter, setIsoFilter] = useState("");

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("devFavorites") || "[]");
    setFavorites(stored);
  }, []);

  const filteredFavorites = favorites.filter(fav => {
    const matchesDilution = !dilutionFilter || 
      fav.dilution?.toLowerCase().includes(dilutionFilter.toLowerCase());
    const matchesISO = !isoFilter || fav.iso === isoFilter;
    return matchesDilution && matchesISO;
  });

  const uniqueISOs = Array.from(new Set(filteredFavorites.map(f => f.iso)))
    .sort((a, b) => a - b);

  const removeFavorite = (fav) => {
    const key = `favorite_${fav.film.film_id}_${fav.developer.dev_id}_${fav.dilution}`;
    const newFavorites = favorites.filter(f => f.key !== key);
    setFavorites(newFavorites);
    localStorage.setItem("devFavorites", JSON.stringify(newFavorites));
    toast.success("Рецепт удалён из избранного");
  };

  const copyRecipe = (result) => {
    const text = `${result.film.film_name} + ${result.developer.dev_name} (${result.dilution})\nISO: ${result.iso}\nВремя: ${result.time_135} (135), ${result.time_120} (120)\nТемпература: ${result.temp_c}°C\n${result.notes || ""}`;
    navigator.clipboard.writeText(text);
    toast.success("Рецепт скопирован!");
  };

    const parseTime = (timeStr) => {
    if (!timeStr) return 0;

    const str = timeStr.toString().trim();

    // 1. Формат "5:30" или "05:30"
    if (str.includes(":")) {
      const [mins, secs] = str.split(":").map(Number);
      return mins + secs / 60;
    }

    // 2. Формат "8-10" — диапазон
    if (str.includes("-")) {
      const [min, max] = str.split("-").map(s => parseFloat(s.trim()));
      if (!isNaN(min) && !isNaN(max)) {
        return (min + max) / 2;
      }
    }

    // 3. Просто число: "8"
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="container-fluid my-4">
      {/* === Заголовок + кнопка "Назад" === */}
      <div className="d-flex align-items-center mb-4">
        <Link to="/" className="btn btn-outline-secondary me-3">
          <BiArrowBack className="me-1" />
          Назад
        </Link>
        <h1 className="mb-0">Избранное</h1>
      </div>

      {favorites.length === 0 ? (
        <div className="alert alert-info">
          У вас пока нет избранных рецептов. Добавляйте их на <Link to="/">главной странице</Link>.
        </div>
      ) : (
        <>
          {/* === Фильтры === */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Поиск по разбавлению (1+1)"
                value={dilutionFilter}
                onChange={(e) => setDilutionFilter(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <select
                className="form-select"
                value={isoFilter}
                onChange={(e) => setIsoFilter(e.target.value)}
              >
                <option value="">Все ISO</option>
                {uniqueISOs.map(iso => (
                  <option key={iso} value={iso}>{iso}</option>
                ))}
              </select>
            </div>
          </div>

          {/* === ДЕСКТОП: таблица === */}
          <div className="d-none d-lg-block">
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Плёнка</th>
                    <th>Проявитель</th>
                    <th>Разбавление</th>
                    <th>ISO</th>
                    <th>Время (135)</th>
                    <th>Температура</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFavorites.map((fav, index) => (
                    <tr key={index}>
                      <td>{fav.film.film_name}</td>
                      <td>{fav.developer.dev_name}</td>
                      <td>
                        <OverlayTrigger placement="top" overlay={<Tooltip>1 часть проявителя + 1 часть воды</Tooltip>}>
                          <span className="text-decoration-underline" style={{ cursor: 'help' }}>{fav.dilution}</span>
                        </OverlayTrigger>
                      </td>
                      <td>{fav.iso}</td>
                      <td>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Пересчитать время при другой температуре</Tooltip>}
                        >
                            <Link
                            to="/calc"
                            state={{
                                calculator: 'temperature',
                                baseTime: parseTime(fav.time_135),
                                baseTemp: fav.temp_c,
                                filmType: 'bw'
                            }}
                            className="text-primary text-decoration-underline"
                            >
                            {fav.time_135}
                            </Link>
                        </OverlayTrigger>
                      </td>
                      <td>{fav.temp_c}°C</td>
                      <td>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Копировать рецепт</Tooltip>}>
                          <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => copyRecipe(fav)}>
                            <BiCopy />
                          </button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Убрать из избранного</Tooltip>}>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => removeFavorite(fav)}>
                            <BiSolidStar />
                          </button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* === МОБИЛЬНЫЕ: карточки === */}
          <div className="d-lg-none">
            {filteredFavorites.map((fav, index) => (
              <div key={index} className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title mb-2">
                    {fav.film.film_name} + {fav.developer.dev_name}
                  </h6>
                  <div className="small text-muted mb-2">
                    <strong>Разбавление:</strong> {fav.dilution} | <strong>ISO:</strong> {fav.iso}
                  </div>
                  <div className="mb-2">
                    <strong>Время (135):</strong>{' '}
                    <Link
                      to="/calc"
                      state={{
                        calculator: 'temperature',
                        baseTime: parseTime(fav.time_135),
                        baseTemp: fav.temp_c,
                        filmType: 'bw'
                      }}
                      className="text-primary text-decoration-underline"
                    >
                      {fav.time_135}
                    </Link>
                  </div>
                  <div className="small text-muted mb-2">
                    <strong>Температура:</strong> {fav.temp_c}°C
                  </div>
                  {fav.notes && (
                    <p className="small text-muted fst-italic mb-2">{fav.notes}</p>
                  )}
                  <div className="d-flex gap-2">
                    <OverlayTrigger placement="top" overlay={<Tooltip>Копировать</Tooltip>}>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => copyRecipe(fav)}>
                        <BiCopy />
                      </button>
                    </OverlayTrigger>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Убрать</Tooltip>}>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => removeFavorite(fav)}>
                        <BiSolidStar />
                      </button>
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 text-muted">
            Всего в избранном: {filteredFavorites.length}
          </div>
        </>
      )}

      <ScrollToTopButton />
    </div>
  );
};

export default Favorites;